// Общото за черновите на листовете на НОВ документ — доставка, изписване,
// прехвърляне, инвентаризация: router state-ът между началния екран и листа,
// обобщението, което отива в списъка на сървъра и в картата на началния екран,
// и локалното копие в localStorage (резерв при прекъсната връзка и чернова до
// BE без черновите). Заявките са в `api/inventoryDrafts.ts`; как се ползват —
// hook-овете в тази папка (листът: `useWorksheetDraft` + `useDraftResume`,
// началният екран: `useHomeDrafts`).
import type { DraftBody, DraftStatus, InventoryDraft } from "../../../../api/inventoryDrafts";

// Изборът, направен на началния екран — листът не пита втори път; черновата
// от сървъра идва с id-то си и с това чия е (своя или поета от колега).
export type DraftRouterState = {
  draftChoice?: "continue" | "discard";
  draftId?: number;
  ownDraft?: boolean;
};

// Всеки payload носи кога и от кого е записан — за въпроса „Да продължите ли…"
export type DraftPayloadBase = { savedAt: string; savedBy: string | null };

// Копието за списъка на сървъра (колоните до payload-а, без да се чете той)
export type DraftSummaryFields = {
  // доставчикът · складът · „Бар → Кухня" — етикетът на картата
  label: string | null;
  // хапчето: фактура/стокова при доставка, изписване/брак при изписване
  documentType: string | null;
  linesCount: number;
  // бруто; null — сумата няма смисъл (инвентаризация)
  total: number | null;
  // складът на листа — САМО за картата на началния екран (свързването
  // „незавършена инвентаризация → склад"); в тялото на записа не пътува —
  // сървърът го чете от payload-а
  warehouseId?: number | null;
};

// Картата на незавършена чернова (моята или на колега) на началния екран
export type DraftSummary = DraftSummaryFields & {
  key: string;
  updatedAt: string;
  ownerName: string | null;
  // busy — друг я държи отворена в последните 2 минути
  status: DraftStatus | "busy";
  // кой я държи отворена (при busy) — „в момента брои Мария"
  lockedByName?: string | null;
  // кога е започната (сървърът; стар BE и локалната — няма)
  createdAt?: string | null;
  problemText?: string | null;
  onOpen: () => void;
};

// Тялото на `PUT /drafts/*`. Етикетът пътува в `supplierName` — така е
// колоната от първия тип (доставка); договорът е в docs/cross-repo-contract.md.
export const draftBodyOf = <P>(
  payload: P,
  summary: DraftSummaryFields,
  state: { status: DraftStatus; problemText: string | null },
): DraftBody<P> => ({
  payload,
  supplierName: summary.label,
  documentType: summary.documentType,
  linesCount: summary.linesCount,
  totalWithVat: summary.total,
  ...state,
});

// Черновата от сървъра → картата
export const summaryOfServerDraft = (draft: InventoryDraft, onOpen: () => void): DraftSummary => ({
  key: `server-${draft.id}`,
  label: draft.supplierName,
  documentType: draft.documentType,
  linesCount: draft.linesCount,
  total: draft.totalWithVat,
  updatedAt: draft.updatedAt ?? "",
  ownerName: draft.ownerName,
  status: draft.lockActive ? "busy" : draft.status,
  lockedByName: draft.lockActive ? draft.lockedByName : null,
  createdAt: draft.createdAt ?? null,
  warehouseId: draft.warehouseId ?? null,
  problemText: draft.problemText,
  onOpen,
});

// ─── Локалното копие ────────────────────────────────────────────────────────
// Пише се само за СВОЯ чернова (поета чужда би презаписала моята в този
// браузър). Хранилището може да липсва/да е пълно — тогава пази само сървърът.
export type LocalDraftStore<P> = {
  read: () => P | null;
  persist: (payload: P) => void;
  clear: () => void;
};

export const localDraftStore = <P>(
  key: string,
  // проверява формата (и вдига стари версии); null — не е чернова от този тип
  parse: (raw: unknown) => P | null,
): LocalDraftStore<P> => ({
  read: () => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? parse(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  },
  persist: (payload) => {
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // пълно/забранено хранилище — черновата остава на сървъра
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(key);
    } catch {
      // забранено хранилище — няма какво да се чисти
    }
  },
});

// Обект от JSON с числово поле `v` от позволените версии — първата проверка на
// всеки `parse`
export const isVersionedObject = (raw: unknown, versions: number[]): raw is { v: number } =>
  !!raw && typeof raw === "object" && versions.includes((raw as { v?: unknown }).v as number);
